import { useState, useCallback, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Ban, CircleCheck, Send, MessageSquare, Trash2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { SectionHeader } from './SectionHeader';
import {
  getCampaignComments,
  addCampaignComment,
  deleteCampaignComment,
  getCampaignEvents,
  deleteCampaignEvent,
  type CampaignComment,
} from '@/api/campaigns';
import { usePermissions } from '@/hooks/usePermissions';
import { useTribute, type MentionData } from '@/hooks/useTribute';
import { formatCommentTime } from '@/utils/date';
import type { Comment } from '../types';
import { PLACEHOLDER_COMMENTS } from '../placeholder';

function formatCommentDate(iso: string): string {
  return formatCommentTime(iso);
}

function renderCommentText(text: string, mentions?: Comment['mentions']): React.ReactNode {
  if (!mentions || mentions.length === 0) return text;
  const names = mentions
    .map((m) => m.name)
    .filter((n): n is string => !!n)
    .sort((a, b) => b.length - a.length);
  if (names.length === 0) return text;
  const pattern = new RegExp(
    `@(${names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`,
    'g',
  );
  const parts: React.ReactNode[] = [];
  let last = 0,
    match: RegExpExecArray | null,
    k = 0;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    parts.push(
      <span key={k++} className="text-blue-600 font-medium bg-blue-50 rounded px-0.5">
        @{match[1]}
      </span>,
    );
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length > 0 ? <>{parts}</> : text;
}

function mapApiComment(c: CampaignComment): Comment {
  const name = c.author_name || c.author_email;
  const parts = name.trim().split(/\s+/);
  const initials =
    parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : name.slice(0, 2).toUpperCase();
  return {
    id: c.id,
    author: name,
    author_email: c.author_email,
    initials,
    date: c.created_at,
    type: c.comment_type,
    text: c.comment_text,
    can_delete: c.can_delete,
    mentions: c.mentions,
  };
}

interface ActivityProps {
  campaignId?: string;
  onApprovalDeleted?: () => void;
}

export function Activity({ campaignId, onApprovalDeleted }: ActivityProps) {
  const queryClient = useQueryClient();
  const [commentText, setCommentText] = useState('');
  const [commentType, setCommentType] = useState<'general' | 'rejection'>('general');
  const [confirmDeleteEventId, setConfirmDeleteEventId] = useState<string | null>(null);
  const [mentions, setMentions] = useState<MentionData[]>([]);

  const handleMentionsChange = useCallback((m: MentionData[]) => setMentions(m), []);
  const handleTributeTextChange = useCallback((v: string) => setCommentText(v), []);

  const { textareaRef, clearMentions, syncMentionsWithText } = useTribute({
    onMentionsChange: handleMentionsChange,
    onTextChange: handleTributeTextChange,
  });

  // Read-only roles can follow the thread but not add to it. Defaults to false
  // while permissions load so the composer never flashes in for a viewer; the
  // API refuses them either way.
  const { data: permissions } = usePermissions();
  const canComment = permissions?.can_comment ?? false;

  const { data: apiData } = useQuery({
    queryKey: ['campaign-comments', campaignId],
    queryFn: () => getCampaignComments(campaignId!, 'asset'),
    enabled: !!campaignId,
  });

  const { data: eventsData } = useQuery({
    queryKey: ['campaign-events', campaignId],
    queryFn: () => getCampaignEvents(campaignId!),
    enabled: !!campaignId,
  });

  const deleteMutation = useMutation({
    mutationFn: (commentId: string) => deleteCampaignComment(campaignId!, commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-comments', campaignId] });
    },
  });

  const deleteEventMutation = useMutation({
    mutationFn: (eventId: string) => deleteCampaignEvent(campaignId!, eventId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-events', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['campaign-assets', campaignId] });
      onApprovalDeleted?.();
    },
  });

  const sendMutation = useMutation({
    mutationFn: (text: string) =>
      addCampaignComment(campaignId!, 'asset', text, commentType, mentions),
    onSuccess: () => {
      setCommentText('');
      clearMentions();
      queryClient.invalidateQueries({ queryKey: ['campaign-comments', campaignId] });
    },
  });

  // Merge comments with asset_approved events, sorted chronologically
  const comments: Comment[] = campaignId
    ? [
        ...(apiData?.comments ?? []).map(mapApiComment),
        ...(eventsData?.events ?? [])
          .filter((e) => e.event_type === 'asset_approved')
          .map((e) => {
            const name = e.actor_name || e.actor_email || 'Unknown';
            const parts = name.trim().split(/\s+/);
            const initials =
              parts.length >= 2
                ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
                : name.slice(0, 2).toUpperCase();
            return {
              id: e.id,
              author: name,
              author_email: e.actor_email ?? undefined,
              initials,
              date: e.created_at,
              type: 'approval' as const,
              text: '',
              can_delete: e.can_delete,
              approvalNote: (e.metadata?.approval_note as string | undefined) ?? '',
            };
          }),
      ].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    : PLACEHOLDER_COMMENTS;

  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [comments.length]);

  const handleSend = () => {
    const trimmed = commentText.trim();
    if (!trimmed || !campaignId) return;
    sendMutation.mutate(trimmed);
  };

  return (
    <div className="flex-1 bg-white rounded-xl border border-slate-200 flex flex-col min-h-0">
      <SectionHeader label={`Activity (${comments.length})`} />

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {comments.map((comment, i) => (
          <motion.div
            key={i}
            className="flex gap-3 group/comment"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: i * 0.1, ease: [0.4, 0, 0.2, 1] }}
            whileHover={{
              scale: 1.04,
              originX: 0,
              transition: { duration: 0.12, ease: 'easeOut' },
            }}
          >
            {comment.type === 'rejection' ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 bg-red-100 cursor-default">
                    <Ban className="w-3.5 h-3.5 text-red-500" />
                  </div>
                </TooltipTrigger>
                <TooltipContent>{comment.author} rejected this</TooltipContent>
              </Tooltip>
            ) : comment.type === 'approval' ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 bg-emerald-100 cursor-default">
                    <CircleCheck className="w-3.5 h-3.5 text-emerald-600" />
                  </div>
                </TooltipTrigger>
                <TooltipContent>{comment.author} approved this</TooltipContent>
              </Tooltip>
            ) : (
              <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 bg-slate-100">
                <MessageSquare className="w-3.5 h-3.5 text-slate-400" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-1.5 mb-1">
                <span className="text-sm font-medium text-slate-700">{comment.author}</span>
                <span className="text-[11px] text-slate-400">
                  {formatCommentDate(comment.date)}
                </span>
              </div>
              {comment.type === 'approval' ? (
                <p className="text-sm text-slate-600 leading-relaxed italic">
                  {comment.approvalNote || (
                    <span className="text-slate-400">No approval note provided.</span>
                  )}
                </p>
              ) : (
                <p className="text-sm text-slate-600 leading-relaxed">
                  {renderCommentText(comment.text, comment.mentions)}
                </p>
              )}
            </div>
            {comment.id && campaignId && comment.type === 'approval' && comment.can_delete && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setConfirmDeleteEventId(comment.id!)}
                    className="flex-shrink-0 self-start mt-0.5 w-6 h-6 flex items-center justify-center rounded-md text-slate-300 hover:text-red-500 hover:bg-red-50 transition-all opacity-0 group-hover/comment:opacity-100"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Delete approval</TooltipContent>
              </Tooltip>
            )}
            {comment.id &&
              campaignId &&
              comment.type !== 'approval' &&
              comment.can_delete &&
              canComment && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => deleteMutation.mutate(comment.id!)}
                      disabled={deleteMutation.isPending}
                      className="flex-shrink-0 self-start mt-0.5 w-6 h-6 flex items-center justify-center rounded-md text-slate-300 hover:text-red-500 hover:bg-red-50 transition-all opacity-0 group-hover/comment:opacity-100 disabled:opacity-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Delete comment</TooltipContent>
                </Tooltip>
              )}
          </motion.div>
        ))}
      </div>

      {!canComment ? (
        <div className="flex-shrink-0 pt-3 pb-3">
          <div className="mx-4 border-t border-slate-200 mb-3" />
          <p className="px-5 text-xs text-slate-400">Your role has view-only access to comments.</p>
        </div>
      ) : (
        <div className="flex-shrink-0 pt-3 pb-3">
          <div className="mx-4 border-t border-slate-200 mb-3" />
          <div className="px-5 space-y-2">
            <textarea
              ref={textareaRef}
              placeholder="Add a comment... Use @ to mention someone"
              rows={2}
              value={commentText}
              onChange={(e) => {
                setCommentText(e.target.value);
                syncMentionsWithText(e.target.value);
              }}
              disabled={!campaignId}
              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#0014cc]/10 focus:border-[#0014cc]/50 resize-none transition-all disabled:opacity-50"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  const tributeMenu = document.querySelector('.tribute-container');
                  if (tributeMenu?.classList.contains('tribute-container-active')) return;
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setCommentType('general')}
                  className={`text-xs font-medium transition-colors ${commentType === 'general' ? 'text-slate-800' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  General
                </button>
                <span className="text-slate-300 text-xs select-none">|</span>
                <button
                  type="button"
                  onClick={() => setCommentType('rejection')}
                  className={`text-xs font-medium transition-colors ${commentType === 'rejection' ? 'text-red-600' : 'text-slate-400 hover:text-red-500'}`}
                >
                  Rejection
                </button>
              </div>
              {(() => {
                const isDisabled = !commentText.trim() || !campaignId || sendMutation.isPending;
                const disabledReason = !campaignId
                  ? 'No campaign selected'
                  : sendMutation.isPending
                    ? 'Sending…'
                    : 'Enter a comment first';
                const btn = (
                  <button
                    onClick={handleSend}
                    disabled={isDisabled}
                    className={`px-2.5 py-1 text-sm font-medium rounded-lg flex items-center gap-1.5 transition-colors active:scale-[0.97] ${
                      isDisabled
                        ? 'bg-white text-slate-400 border border-slate-200 cursor-not-allowed'
                        : commentType === 'rejection'
                          ? 'bg-red-600 hover:bg-red-700 text-white'
                          : 'bg-[#0014cc] hover:bg-[#0014cc]/90 text-white'
                    }`}
                  >
                    <Send className="w-3.5 h-3.5" />
                    <span>{commentType === 'rejection' ? 'Send Rejection' : 'Send Comment'}</span>
                  </button>
                );
                return isDisabled ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>{btn}</span>
                    </TooltipTrigger>
                    <TooltipContent>{disabledReason}</TooltipContent>
                  </Tooltip>
                ) : (
                  btn
                );
              })()}
            </div>
          </div>
        </div>
      )}
      {confirmDeleteEventId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-[400px] bg-white rounded-xl border border-slate-200 shadow-xl overflow-hidden">
            <div className="px-6 py-3 border-b border-slate-200 bg-slate-50">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                Delete Approval
              </p>
            </div>
            <div className="px-6 py-5">
              <p className="text-sm text-slate-600 mb-5">
                This approval record will be permanently deleted and assets will be reset to draft
                for re-approval.
              </p>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setConfirmDeleteEventId(null)}
                  className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  disabled={deleteEventMutation.isPending}
                  onClick={() => {
                    deleteEventMutation.mutate(confirmDeleteEventId);
                    setConfirmDeleteEventId(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
